/**
 * Crece — interactive product tour.
 *
 * • Auto-starts the first time an authenticated user lands inside the app shell
 *   (gated by `crece_tour_seen_<role>` in localStorage).
 * • Replayable any time via `useTour().start()` — wired to a (?) icon in the
 *   topbar and a "Take the tour again" button on the Profile page.
 * • Tour steps target the existing `data-testid` selectors so we don't have to
 *   re-tag the UI. Different step lists per role (partner / operations / admin).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Joyride, EVENTS, STATUS, ACTIONS } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const TourCtx = createContext({ start: () => {} });

export function useTour() {
  return useContext(TourCtx);
}

// Helper: build a single step. `route` (optional) navigates first before showing.
const step = (target, title, body, opts = {}) => ({
  target,
  title,
  content: body,
  skipBeacon: true,                                  // v3 prop name (was disableBeacon in v2)
  placement: opts.placement || "auto",
  route: opts.route,
});

// ---- Partner tour (8 stops) ----
const PARTNER_STEPS = [
  step('[data-testid="kpi-wallet"]', "Your wallet at a glance",
    "Every commission you earn lands here. Click 'Withdraw' anytime to move funds to your verified bank account.",
    { route: "/app/dashboard" }),
  step('[data-testid="profile-status-banner"]', "Complete your KYC",
    "Upload PAN, Aadhaar (front + back), and a cancelled cheque. Operations verifies in ~24h and you'll see 'Verified ✓' here.",
    { route: "/app/profile" }),
  step('[data-testid="nav-team"]', "Build your team",
    "Recruit other consultants under you. Every lead they close earns you an override commission.",
    { route: "/app/dashboard" }),
  step('[data-testid="nav-leads"]', "Track every client",
    "Add prospects, watch them move New → Approved → Paid. Commissions auto-credit to your wallet on Paid.",
    { route: "/app/dashboard" }),
  step('[data-testid="nav-withdraw"]', "Wallet & Withdraw",
    "Request NEFT withdrawals here once your KYC is Verified.",
    { route: "/app/dashboard" }),
  step('[data-testid="nav-certificates"]', "Earn certificates",
    "Promotion certificates (when you rank up) and training certificates (when you finish courses) — sharable + downloadable.",
    { route: "/app/dashboard" }),
  step('[data-testid="nav-training"]', "Training & Need-Analysis",
    "Run gamified financial-needs questionnaires with prospects and take training exams to unlock new certificates.",
    { route: "/app/dashboard" }),
  step('[data-testid="storefront-url"]', "Share your storefront",
    "Send this link to prospects — they see your branded product list and book directly under your referral code.",
    { route: "/app/dashboard" }),
];

// ---- Operations tour ----
const OPS_STEPS = [
  step('[data-testid="ops-view-profiles"]', "Profile Approvals",
    "All partner KYC requests land here. Use the 5-state filter chips (New registration, Documents pending, Under review, Approved, Verified, Rejected) to triage.",
    { route: "/operations/dashboard" }),
  step('[data-testid="ops-filter-under-review"]', "The action queue",
    "Partners who've uploaded all four docs land in 'Under review'. Click a row → review docs → tick PAN / Bank / Mobile in the side panel.",
    { route: "/operations/dashboard" }),
  step('[data-testid="ops-view-business"]', "Business Verification",
    "When a partner books a real client lead, verify the business + amount here. This unlocks the partner's commission.",
    { route: "/operations/dashboard" }),
  step('[data-testid="ops-view-tasks"]', "My Tasks",
    "Auto-generated to-dos from the system (e.g. 'Review KYC for X', 'Verify lead amount for Y'). Clear them as you work.",
    { route: "/operations/dashboard" }),
  step('[data-testid="ops-tour-trigger"]', "Replay the tour anytime",
    "Hit this (?) any time you want this walkthrough again.",
    { route: "/operations/dashboard" }),
];

// ---- Admin tour ----
const ADMIN_STEPS = [
  step('[data-testid="admin-tab-dashboard"]', "Live business pulse",
    "Lead funnel, timeline, top channel partners, KYC funnel — refreshed every load.",
    { route: "/app/admin" }),
  step('[data-testid="admin-tab-users"]', "Partner roster",
    "Every consultant in the network. Promote ranks, view downline trees, suspend accounts.",
    { route: "/app/admin" }),
  step('[data-testid="admin-tab-products"]', "Product catalogue",
    "Add new financial products, set commission %, channel partner, KYC requirements, and visibility.",
    { route: "/app/admin" }),
  step('[data-testid="admin-tab-analytics"]', "Deep analytics",
    "Drill into commissions paid, share-link funnels, withdrawal SLAs, lead category split, and more.",
    { route: "/app/admin" }),
  step('[data-testid="admin-tab-settings"]', "Settings & branding",
    "MLM levels & %, SMTP config, certificate branding — everything that makes the network 'yours'.",
    { route: "/app/admin" }),
  step('[data-testid="admin-tab-announcements"]', "Announce new features",
    "Compose a one-off feature/news email, choose who receives it (all / partners / approved partners / ops), preview, then send in one click.",
    { route: "/app/admin" }),
];

const STEPS_BY_ROLE = {
  partner: PARTNER_STEPS,
  operations: OPS_STEPS,
  admin: ADMIN_STEPS,
  cxo: ADMIN_STEPS,   // CXOs run the admin tour — they live in the admin panel
  ceo: ADMIN_STEPS,   // CEO sees the admin tour by default; impersonating swaps user.role to partner anyway
  finance: [],        // no tour — must never hijack finance staff into the partner shell
};

// Crece green styling for the Joyride tooltip (v3 — styles only for visual look)
const TOUR_STYLES = {
  tooltip: { borderRadius: 16, fontFamily: "inherit" },
  buttonNext: { backgroundColor: "#064e3b", borderRadius: 999, padding: "8px 18px", fontWeight: 700 },
  buttonBack: { color: "#44403c", marginRight: 8 },
  buttonSkip: { color: "#a8a29e" },
};

// v3 Options prop — buttons, beacon skip, colours, etc.
const TOUR_OPTIONS = {
  skipBeacon: true,
  buttons: ["back", "skip", "primary"],
  showProgress: true,
  primaryColor: "#064e3b",
  backgroundColor: "#ffffff",
  textColor: "#1c1917",
  arrowColor: "#ffffff",
  overlayColor: "rgba(12, 31, 26, 0.55)",
  zIndex: 10000,
};

const LOCALES = { back: "Back", close: "Close", last: "Finish tour", next: "Next", skip: "Skip tour" };

export function TourProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [running, setRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const role = user?.role || "partner";
  const steps = useMemo(() => STEPS_BY_ROLE[role] || PARTNER_STEPS, [role]);
  const storageKey = `crece_tour_seen_${role}`;

  // Routes where the tour MAY auto-start. Anywhere else (public storefront `/c/`,
  // referral landing `/r/`, marketing `/`, auth pages, login pages, …) the tour
  // would just try to navigate to /app/* and yank the visitor away from the
  // public page they were looking at — so we never auto-fire there.
  const isShellRoute = useCallback((path) => {
    return path.startsWith("/app") || path.startsWith("/operations/dashboard") || path.startsWith("/finance/dashboard");
  }, []);

  const finish = useCallback(() => {
    setRunning(false);
    setStepIndex(0);
    try { localStorage.setItem(storageKey, "true"); } catch { /* private mode */ }
  }, [storageKey]);

  // Auto-start exactly once per role, after the user has loaded — but ONLY when
  // the user is sitting inside the app shell. This prevents the tour from
  // closing a partner's published storefront / referral landing when they
  // happen to be logged in in the same browser session.
  useEffect(() => {
    if (!user) return;
    if (user.corporate_id) return; // corporate employees: partner tour points at pages they can't see
    if (!steps.length) return;
    if (!isShellRoute(location.pathname)) return;
    if (steps[0]?.route && !location.pathname.startsWith(steps[0].route)) return; // deep links (e.g. /app/policy-lens) are never hijacked
    try {
      if (localStorage.getItem(storageKey) === "true") return;
    } catch { /* private mode → still allow */ }
    const t = setTimeout(() => { setStepIndex(0); setRunning(true); }, 700);
    return () => clearTimeout(t);
  }, [user, storageKey, location.pathname, isShellRoute, steps]);

  // Public API: start() — manual replay. Forces the user into /app/dashboard
  // (or admin) before reopening so the very first selector is on screen.
  const start = useCallback(() => {
    if (!isShellRoute(location.pathname)) {
      const home = role === "admin" || role === "ceo" || role === "cxo" ? "/app/admin"
        : role === "operations" ? "/operations/dashboard"
        : "/app/dashboard";
      navigate(home);
    }
    setStepIndex(0);
    setTimeout(() => setRunning(true), 400);
  }, [isShellRoute, location.pathname, role, navigate]);

  const handleEvent = useCallback((data) => {
    const { type, status, action, step: stepData } = data;
    const idx = stepData?.index ?? stepIndex;
    const isLast = idx >= steps.length - 1;

    // 1) User clicked Skip → done.
    if (action === ACTIONS.SKIP || status === STATUS.SKIPPED) { finish(); return; }
    // 2) User clicked the (×) close icon → done.
    if (action === ACTIONS.CLOSE) { finish(); return; }
    // 3) Joyride reports the tour fully finished → done.
    if (status === STATUS.FINISHED) { finish(); return; }
    // 4) User clicked "Got it" / Next on the last step → finalize ourselves
    //    (in controlled mode Joyride won't re-emit FINISHED automatically here).
    if (type === EVENTS.STEP_AFTER && isLast && action === ACTIONS.NEXT) { finish(); return; }

    // 5) Normal advance — handle route hop for the *next* step.
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = idx + 1;
      if (nextIndex >= steps.length) { finish(); return; }
      const nextStep = steps[nextIndex];
      // Refuse to yank the user away from a non-app route (e.g. they're on
      // their public storefront `/c/CODE` — never navigate to /app from there).
      if (!isShellRoute(location.pathname)) { finish(); return; }
      if (nextStep.route && !location.pathname.startsWith(nextStep.route)) {
        setRunning(false);
        setStepIndex(nextIndex);
        navigate(nextStep.route);
        setTimeout(() => setRunning(true), 700);
      } else {
        setStepIndex(nextIndex);
      }
    }
  }, [steps, location.pathname, navigate, stepIndex, finish, isShellRoute]);

  return (
    <TourCtx.Provider value={{ start }}>
      {children}
      {/* Mount Joyride only when it's actually running — keeps the overlay
          (zIndex 10000) from intercepting clicks on the rest of the app. */}
      {running && steps.length > 0 && (
        <Joyride
          steps={steps}
          run={running}
          stepIndex={stepIndex}
          continuous
          scrollToFirstStep
          locale={LOCALES}
          options={TOUR_OPTIONS}
          styles={TOUR_STYLES}
          onEvent={handleEvent}
        />
      )}
    </TourCtx.Provider>
  );
}
